import { useContainerBlockContext } from '@lighthouse/block'
import type { Sorter } from '@lighthouse/core'
import { getWithScopeId, useAtomAction } from '@lighthouse/shared'
import isFastEqual from 'fast-deep-equal'
import { useAtomValue } from 'jotai'
import { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'

import { sortsCacheAtomFamily } from '@/atoms/storage/state'

interface useSortPayload {
    appId: string
    envId: string
    blockId: string
    canSort: boolean | undefined
    sorts: Sorter[] | undefined
}

export const useSort = (payload: useSortPayload) => {
    const { appId, envId, blockId, canSort, sorts: payloadSort = [] } = payload
    const { scope } = useContainerBlockContext()
    const withScopeId = getWithScopeId(blockId, scope)
    const { run: setSortsCache } = useAtomAction(sortsCacheAtomFamily({ appId, envId, id: withScopeId }))
    const sortCache = useAtomValue(sortsCacheAtomFamily({ appId, envId, id: withScopeId }))
    const [sort, setSort] = useState(sortCache)
    const handleSorterChange = useCallback(
        (data?: Sorter[]) => {
            if (!data) {
                return
            }
            setSortsCache(data)
        },
        [setSortsCache]
    )

    useUpdateEffect(() => {
        if (!isFastEqual(sort, sortCache)) {
            setSort(sortCache)
        }
    }, [sortCache])

    const mergeSort = useMemo(() => {
        if (canSort) {
            const map = new Map()
            sort.forEach(item => {
                map.set(item.id, item)
            })
            payloadSort.forEach(item => {
                map.set(item.id, item)
            })
            return [...map.values()]
        }
        return payloadSort
    }, [canSort, payloadSort, sort])

    return useMemo(() => ({ mergeSort, sortCache: sort, handleSorterChange }), [handleSorterChange, mergeSort, sort])
}
